import {
  LOADING_SHIMMER_BG_COLOR,
  LOADING_SHIMMER_HIGHLIGHT_COLOR,
  LOADING_SHIMMER_SPEED,
} from "@components/Accesses/AccessDetails/config";
import LBox from "@components/LBox";
import LIcon from "@components/LIcon";
import LText from "@components/LText";
import type { AccessGemEconomicsQuoteWire } from "@libs/wire";
import { storeMoneyMinor } from "@libs/platform-wire";
import {
  formatGbpMinorUnits,
  formatMusicAccessPrice,
  getStatusPriceLabel,
} from "@libs/shared-ui";
import { theme } from "@theme";
import { useState } from "react";
import SkeletonPlaceholder from "react-native-skeleton-placeholder";

type PriceProps = {
  price: number | null;
  /** `price` is the lowest of several ticket prices: render "From …". */
  from?: boolean;
  gemEconomicsQuote?: AccessGemEconomicsQuoteWire | null;
  /** The price as its own page words it (a merch product's
   *  `merchPriceLabel`, as Link in Bio says it), in place of `price`'s. */
  label?: string | null;
};

const DEFAULT_CASH_ICON_COLOR = theme.colors.whiteTranslucent50;
const GEM_PURPLE = "#C084FC";
const GEM_PURPLE_TEXT = "#E9D5FF";

const ORIGINAL_PRICE_STYLE = {
  color: theme.colors.whiteTranslucent50,
  letterSpacing: 0,
  textDecorationLine: "line-through" as const,
};

const STATUS_PRICE_PILL_STYLE = {
  backgroundColor: "rgba(126, 87, 194, 0.18)",
  borderColor: "rgba(168, 85, 247, 0.55)",
};

const Price = ({
  price,
  from = false,
  gemEconomicsQuote,
  label,
}: PriceProps) => {
  const [discountPriceWidth, setDiscountPriceWidth] = useState(0);

  if (price === null) {
    return null;
  }

  const hasAppliedDiscount =
    gemEconomicsQuote?.applied === true &&
    gemEconomicsQuote.appliedDiscountMinorUnits > 0;

  if (hasAppliedDiscount) {
    const originalPriceText = formatGbpMinorUnits(
      gemEconomicsQuote.purchasePriceMinorUnits,
    );
    const statusPriceLabel = getStatusPriceLabel(gemEconomicsQuote);

    return (
      <LBox
        height={20}
        justifyContent="center"
        style={{ overflow: "visible", position: "relative" }}
      >
        <LBox
          alignItems="center"
          flexDirection="row"
          gap="s1_5"
          onLayout={(event) => {
            setDiscountPriceWidth(event.nativeEvent.layout.width);
          }}
          style={{ alignSelf: "flex-start" }}
        >
          <LIcon colors={{ color: GEM_PURPLE }} name="cash" size={16} />
          <LBox
            alignItems="baseline"
            flexDirection="row"
            flexShrink={1}
            flexWrap="wrap"
            gap="s2"
          >
            <LText variant="Body/B1/Medium" style={ORIGINAL_PRICE_STYLE}>
              {originalPriceText}
            </LText>
            <LText
              adjustsFontSizeToFit
              numberOfLines={1}
              variant="Body/B1/Medium"
              style={{
                color: GEM_PURPLE,
                letterSpacing: 0,
              }}
            >
              {storeMoneyMinor(
                gemEconomicsQuote.amountDueMinorUnits,
                gemEconomicsQuote.currency,
              )}
            </LText>
          </LBox>
        </LBox>

        <LBox
          alignItems="center"
          borderRadius="max"
          borderWidth={1}
          flexDirection="row"
          gap="s1"
          px="s2"
          py="s1"
          style={STATUS_PRICE_PILL_STYLE}
          position="absolute"
          left={discountPriceWidth + theme.spacing.s2}
          top={-4}
        >
          <LIcon name="gem" size={14} />
          <LText
            flexShrink={1}
            numberOfLines={1}
            variant="Body/B2/SemiBold"
            style={{
              color: GEM_PURPLE_TEXT,
              letterSpacing: 0,
            }}
          >
            {statusPriceLabel}
          </LText>
        </LBox>
      </LBox>
    );
  }

  const isFree = price <= 0;
  const priceText =
    label ?? `${from && !isFree ? "From " : ""}${formatMusicAccessPrice(price)}`;
  return (
    <LBox flexDirection="row" alignItems="center" gap="s1_5">
      <LIcon
        colors={{ color: DEFAULT_CASH_ICON_COLOR }}
        name="cash"
        size={16}
      />
      {isFree ? (
        // Pill backdrop so FREE pops, matching the LIB sheet treatment.
        <LBox
          borderRadius="max"
          borderWidth={1}
          px="s2_5"
          py="s0_5"
          style={STATUS_PRICE_PILL_STYLE}
        >
          <LText
            variant="Body/B1/SemiBold"
            style={{ color: GEM_PURPLE_TEXT, letterSpacing: 0.6 }}
          >
            {priceText}
          </LText>
        </LBox>
      ) : (
        <LText
          variant="Body/B1/Medium"
          style={{ color: GEM_PURPLE, letterSpacing: 0 }}
        >
          {priceText}
        </LText>
      )}
    </LBox>
  );
};

const Loading = () => {
  return (
    <LBox flexDirection="row" alignItems="center" gap="s1_5">
      <SkeletonPlaceholder
        highlightColor={LOADING_SHIMMER_HIGHLIGHT_COLOR}
        backgroundColor={LOADING_SHIMMER_BG_COLOR}
        speed={LOADING_SHIMMER_SPEED}
        borderRadius={theme.borderRadii.max}
      >
        <SkeletonPlaceholder.Item
          width={14}
          height={14}
          borderRadius={theme.borderRadii.max}
        />
      </SkeletonPlaceholder>
      <SkeletonPlaceholder
        highlightColor={LOADING_SHIMMER_HIGHLIGHT_COLOR}
        backgroundColor={LOADING_SHIMMER_BG_COLOR}
        speed={LOADING_SHIMMER_SPEED}
        borderRadius={theme.borderRadii.max}
      >
        <SkeletonPlaceholder.Item
          width={100}
          height={theme.textVariants["Body/B1/Medium"].lineHeight}
          borderRadius={theme.borderRadii.max}
        />
      </SkeletonPlaceholder>
    </LBox>
  );
};
export default {
  Price,
  Loading,
};
